import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  getFbAdAccounts,
  saveFbAdAccount,
  getFbMetaCampaigns,
  syncFbMetaCampaigns,
  setupFbMetaCampaign,
  getProjectsByCompanyId,
  getAllSalesUsersByProjectId
} from '../../services/api/implementation/impl';

const MULTISELECT_STYLE = {
  chips: { background: '#2A565E', color: 'white' },
  searchBox: {
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '8px',
    background: 'white',
    color: '#333'
  },
  input: { color: '#333' },
  option: { color: '#333', background: 'white' },
  listItem: { color: '#333', background: 'white' }
};

const normalizeCampaigns = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.campaigns)) return data.campaigns;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getFbPermissionError = (err) => {
  if (err?.response?.status === 403) {
    return 'Facebook permission missing — reconnect with ads access';
  }
  return err?.response?.data?.message || err?.message || 'Something went wrong';
};

const campaignIsSetup = (campaign) =>
  Boolean(campaign?.pre_lead_list_id || campaign?.pre_lead_list?.id);

const campaignProjectId = (campaign) =>
  campaign?.project_id ?? campaign?.project?.id ?? null;

const campaignSalesPersons = (campaign) => {
  if (Array.isArray(campaign?.sales_persons) && campaign.sales_persons.length) {
    return campaign.sales_persons;
  }
  if (Array.isArray(campaign?.sales) && campaign.sales.length) {
    return campaign.sales;
  }
  return [];
};

const CampaignSetupRow = ({
  campaign,
  projects,
  companyId,
  onSaved,
  savingId,
  setSavingId
}) => {
  const isDone = campaignIsSetup(campaign);
  const initialProjectId = campaignProjectId(campaign) || '';
  const initialSales = campaignSalesPersons(campaign);

  const [projectId, setProjectId] = useState(String(initialProjectId || ''));
  const [selectedSales, setSelectedSales] = useState(initialSales);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setProjectId(String(campaignProjectId(campaign) || ''));
    setSelectedSales(campaignSalesPersons(campaign));
    setDirty(false);
  }, [campaign.id, campaign.pre_lead_list_id, campaign.project_id, campaign.synced_at]);

  const { data: salesData, isPending: isSalesPending } = useQuery({
    queryKey: ['salesUsers', projectId],
    queryFn: () => getAllSalesUsersByProjectId(projectId, 1, 500),
    enabled: !!projectId
  });

  const salesOptions = Array.isArray(salesData?.data)
    ? salesData.data
    : Array.isArray(salesData)
      ? salesData
      : [];

  const { mutate: saveSetup } = useMutation({
    mutationFn: () => {
      const payload = {
        company_id: Number(companyId),
        project_id: Number(projectId)
      };
      const salesIds = selectedSales.map((p) => p.id).filter(Boolean);
      if (salesIds.length) payload.sales_ids = salesIds;
      return setupFbMetaCampaign(campaign.id, payload);
    },
    onSuccess: (data) => {
      toast.success('Campaign linked to project');
      setSavingId(null);
      setDirty(false);
      onSaved(data, campaign.id);
    },
    onError: (err) => {
      setSavingId(null);
      toast.error(getFbPermissionError(err));
    }
  });

  const isSaving = savingId === campaign.id;
  const statusKey = String(campaign.status || '').toUpperCase() === 'PAUSED' ? 'PAUSED' : 'ACTIVE';

  return (
    <tr className="border-t border-stone-100 align-top">
      <td className="px-3 py-2.5 font-medium text-gray-900">
        <div className="flex flex-col gap-1">
          <span>{campaign.name || '—'}</span>
          {isDone ? (
            <span className="inline-flex w-fit items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700">
              <Check className="w-3 h-3" />
              Done
            </span>
          ) : (
            <span className="inline-flex w-fit text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
              Needs setup
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{statusKey}</td>
      <td className="px-3 py-2.5 min-w-[180px]">
        <select
          className="w-full p-1.5 border border-stone-300 rounded text-sm"
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value);
            setSelectedSales([]);
            setDirty(true);
          }}>
          <option value="">Select project</option>
          {(projects || []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2.5 min-w-[220px]">
        <Multiselect
          options={salesOptions}
          selectedValues={projectId ? selectedSales : []}
          onSelect={(list) => {
            setSelectedSales(list);
            setDirty(true);
          }}
          onRemove={(list) => {
            setSelectedSales(list);
            setDirty(true);
          }}
          displayValue="name"
          placeholder={projectId ? (isSalesPending ? 'Loading…' : 'Optional') : 'Pick project first'}
          disable={!projectId || isSaving}
          style={MULTISELECT_STYLE}
        />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Button
          displayType="secondary"
          disabled={isSaving || !projectId || (isDone && !dirty)}
          onClick={() => {
            if (!projectId) {
              toast.error('Select a project');
              return;
            }
            setSavingId(campaign.id);
            saveSetup();
          }}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </td>
    </tr>
  );
};

/**
 * Meta Ads setup: link ads account → sync campaigns → assign project (+ optional sales).
 * initialStep: 'account' | 'campaigns' — which section to emphasize on open.
 */
const MetaAdsSetupModal = ({ companyId, onClose, initialStep = 'account' }) => {
  const queryClient = useQueryClient();
  const [pickedAccountId, setPickedAccountId] = useState(null);
  const [changingAccount, setChangingAccount] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const campaignsQueryKey = ['fb-meta-campaigns', companyId];

  const {
    data: adAccountsData,
    isPending: isAccountsPending,
    isError: isAccountsError,
    error: accountsError,
    refetch: refetchAccounts
  } = useQuery({
    queryKey: ['fb-ad-accounts', companyId],
    queryFn: () => getFbAdAccounts(companyId),
    enabled: !!companyId
  });

  const {
    data: campaignsData,
    isPending: isCampaignsPending,
    refetch: refetchCampaigns
  } = useQuery({
    queryKey: campaignsQueryKey,
    queryFn: () => getFbMetaCampaigns(companyId),
    enabled: !!companyId,
    staleTime: 0
  });

  const { data: projects = [], isPending: isProjectsPending } = useQuery({
    queryKey: ['project_byId', companyId],
    queryFn: () => getProjectsByCompanyId(companyId),
    enabled: !!companyId
  });

  const accounts = useMemo(
    () => (Array.isArray(adAccountsData?.accounts) ? adAccountsData.accounts : []),
    [adAccountsData]
  );
  const selected = adAccountsData?.selected ?? null;
  const selectedAccount =
    typeof selected === 'object' && selected
      ? selected
      : accounts.find((a) => a.selected) ||
        accounts.find((a) => a.id === selected) ||
        null;

  const campaigns = useMemo(() => normalizeCampaigns(campaignsData), [campaignsData]);
  const hasSelectedAccount = Boolean(selectedAccount || (selected && selected !== null));
  const showPicker = !hasSelectedAccount || changingAccount;

  const setupDoneCount = campaigns.filter(campaignIsSetup).length;

  useEffect(() => {
    if (showPicker && accounts.length && !pickedAccountId) {
      const preferred =
        accounts.find((a) => a.selected)?.id ||
        selectedAccount?.id ||
        accounts[0]?.id ||
        null;
      setPickedAccountId(preferred);
    }
  }, [showPicker, accounts, pickedAccountId, selectedAccount]);

  useEffect(() => {
    if (isAccountsError && accountsError?.response?.status === 403) {
      toast.error('Facebook permission missing — reconnect with ads access');
    }
  }, [isAccountsError, accountsError]);

  const { mutate: saveAccount, isPending: isSavingAccount } = useMutation({
    mutationFn: (payload) => saveFbAdAccount(payload),
    onSuccess: async () => {
      toast.success('Ad account saved');
      setChangingAccount(false);
      await queryClient.invalidateQueries({ queryKey: ['fb-ad-accounts', companyId] });
      refetchAccounts();
    },
    onError: (err) => {
      toast.error(getFbPermissionError(err));
    }
  });

  const { mutate: syncCampaigns, isPending: isSyncing } = useMutation({
    mutationFn: () => syncFbMetaCampaigns({ company_id: companyId }),
    onSuccess: async (data) => {
      let list = normalizeCampaigns(data);
      if (list.length) {
        queryClient.setQueryData(campaignsQueryKey, data?.campaigns ? data : list);
      } else {
        await queryClient.invalidateQueries({ queryKey: campaignsQueryKey });
        const fresh = await refetchCampaigns();
        list = normalizeCampaigns(fresh?.data);
      }
      toast.success(`Synced ${data?.campaigns_upserted ?? list.length} campaigns`);
    },
    onError: (err) => {
      toast.error(getFbPermissionError(err));
    }
  });

  const handleSaveAccount = () => {
    const account = accounts.find((a) => a.id === pickedAccountId);
    if (!account) {
      toast.error('Select an ad account');
      return;
    }
    saveAccount({
      company_id: companyId,
      meta_ad_account_id: account.id,
      name: account.name,
      currency: account.currency
    });
  };

  const handleCampaignSaved = (updated, campaignId) => {
    queryClient.setQueryData(campaignsQueryKey, (prev) => {
      const prevList = normalizeCampaigns(prev);
      const nextCampaign =
        updated?.id
          ? updated
          : Array.isArray(updated)
            ? updated.find((c) => String(c.id) === String(campaignId))
            : null;
      if (!nextCampaign || !prevList.length) {
        refetchCampaigns();
        return prev;
      }
      const nextList = prevList.map((c) =>
        String(c.id) === String(nextCampaign.id) ? { ...c, ...nextCampaign } : c
      );
      if (Array.isArray(prev)) return nextList;
      if (prev && Array.isArray(prev.campaigns)) return { ...prev, campaigns: nextList };
      return nextList;
    });
  };

  const stepBEnabled = hasSelectedAccount && !changingAccount;

  const connectedLabel = selectedAccount
    ? `Linked: ${selectedAccount.name} (${selectedAccount.id || selectedAccount.meta_ad_account_id || selected})`
    : selected
      ? `Linked: ${selected}`
      : null;

  return (
    <Modal width={90} name="Set up Facebook ads" onClose={onClose} isDirty={false}>
      <div className="flex flex-col gap-8">
        {/* Step: Link ads account */}
        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Link ads account</h3>
            <p className="text-sm text-gray-500 mt-1">
              Use the same account as Ads Manager (the one with live spend).
            </p>
          </div>

          {isAccountsPending ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading ad accounts…
            </div>
          ) : isAccountsError && accountsError?.response?.status === 403 ? (
            <p className="text-sm text-rose-600">
              Facebook permission missing — reconnect with ads access
            </p>
          ) : !accounts.length ? (
            <p className="text-sm text-gray-600">No ad accounts on this Facebook user</p>
          ) : showPicker ? (
            <>
              <div className="flex flex-col gap-2 max-h-56 overflow-y-auto border border-stone-200 rounded-lg p-2">
                {accounts.map((account) => {
                  const isReadOnly = String(account.name || '').includes('Read-Only');
                  return (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border ${
                        pickedAccountId === account.id
                          ? 'border-primary bg-[#E9F5F6]'
                          : 'border-transparent hover:bg-gray-50'
                      }`}>
                      <input
                        type="radio"
                        name="meta-ad-account"
                        checked={pickedAccountId === account.id}
                        onChange={() => setPickedAccountId(account.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{account.name}</span>
                          {isReadOnly ? (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
                              Read-Only
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {account.currency || '—'} · {account.id}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2">
                {changingAccount ? (
                  <Button
                    displayType="secondary"
                    onClick={() => setChangingAccount(false)}
                    disabled={isSavingAccount}>
                    Cancel
                  </Button>
                ) : null}
                <Button onClick={handleSaveAccount} disabled={isSavingAccount || !pickedAccountId}>
                  {isSavingAccount ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-gray-800">{connectedLabel}</p>
              <Button
                displayType="secondary"
                onClick={() => {
                  setChangingAccount(true);
                  setPickedAccountId(selectedAccount?.id || null);
                }}>
                Change account
              </Button>
            </div>
          )}
        </section>

        {/* Step: Sync + link campaigns to projects */}
        <section
          className={`flex flex-col gap-3 ${
            stepBEnabled ? '' : 'opacity-50 pointer-events-none'
          }`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Link campaigns to projects</h3>
              <p className="text-sm text-gray-500 mt-1">
                Sync campaigns from Meta, then assign a project (and optional sales) so preleads
                &amp; cost per prelead are tracked per campaign.
              </p>
              {campaigns.length > 0 ? (
                <p className="text-xs text-gray-500 mt-1">
                  {setupDoneCount} of {campaigns.length} campaigns linked
                </p>
              ) : null}
            </div>
            {stepBEnabled ? (
              <Button onClick={() => syncCampaigns()} disabled={isSyncing}>
                {isSyncing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Syncing…
                  </span>
                ) : (
                  'Sync campaigns'
                )}
              </Button>
            ) : null}
          </div>

          {stepBEnabled && isCampaignsPending ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading campaigns…
            </div>
          ) : null}

          {stepBEnabled && !isCampaignsPending && campaigns.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-300 bg-gray-50 px-4 py-6 text-center">
              <p className="text-sm text-gray-600 mb-4">No campaigns synced yet</p>
              <Button onClick={() => syncCampaigns()} disabled={isSyncing}>
                {isSyncing ? 'Syncing…' : 'Sync campaigns'}
              </Button>
            </div>
          ) : null}

          {stepBEnabled && campaigns.length > 0 ? (
            <div className="overflow-x-auto border border-stone-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Campaign</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 font-semibold">Project</th>
                    <th className="px-3 py-2 font-semibold">Sales</th>
                    <th className="px-3 py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isProjectsPending ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                        Loading projects…
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((campaign) => (
                      <CampaignSetupRow
                        key={campaign.id}
                        campaign={campaign}
                        projects={projects}
                        companyId={companyId}
                        onSaved={handleCampaignSaved}
                        savingId={savingId}
                        setSavingId={setSavingId}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}

          {!stepBEnabled ? (
            <p className="text-sm text-gray-500">Link an ads account first to sync campaigns.</p>
          ) : null}
        </section>
      </div>
    </Modal>
  );
};

export default MetaAdsSetupModal;
